/**
 * What the settings drawer shows, and what it lets you change (SPEC-018).
 *
 * SPEC-016 asks about notifications exactly once, from a card that disappears
 * the moment it is answered. That was right for the asking — an offer that
 * follows you around is a demand — and wrong for everything after it. A visitor
 * who tapped "Not now" had no way back, a visitor who tapped "Notify me" had no
 * way to tell whether it had worked, and neither could see whether the thing was
 * on. This module is the answer to "so what IS the state?".
 *
 * The one hard fact it is built around: **a page cannot revoke notification
 * permission.** `Notification.requestPermission()` is one-way, and there is no
 * counterpart. So "off" cannot mean "permission withdrawn" — it has to mean
 * something this app actually controls, which is whether it SENDS. That is a
 * local preference, and the copy says so rather than implying a power over the
 * browser that no page has.
 *
 * Pure: every browser fact arrives as an argument.
 */

import { INSTALL_SUPPORT, installHowToKey } from './installOffer';
import { NOTIFICATION_STATUS } from './notificationOffer';

/** The five things the drawer can say about notifications. */
export const NOTIFICATION_SETTING = {
    on: 'on',
    paused: 'paused',
    off: 'off',
    blocked: 'blocked',
    needsInstall: 'needs-install',
    unavailable: 'unavailable',
};

const NOTIFICATION_COPY = {
    [NOTIFICATION_SETTING.on]: { stateKey: 'settings.notify.state.on', hintKey: 'settings.notify.hint.on' },
    [NOTIFICATION_SETTING.paused]: { stateKey: 'settings.notify.state.paused', hintKey: 'settings.notify.hint.paused' },
    [NOTIFICATION_SETTING.off]: { stateKey: 'settings.notify.state.off', hintKey: 'settings.notify.hint.off' },
    [NOTIFICATION_SETTING.blocked]: { stateKey: 'settings.notify.state.blocked', hintKey: 'settings.notify.hint.blocked' },
    [NOTIFICATION_SETTING.needsInstall]: { stateKey: 'settings.notify.state.needsInstall', hintKey: 'settings.notify.hint.needsInstall' },
    [NOTIFICATION_SETTING.unavailable]: { stateKey: 'settings.notify.state.unavailable', hintKey: 'settings.notify.hint.unavailable' },
};

/**
 * @param {object} input
 * @param {string} input.status The Notification API's permission, normalised.
 * @param {boolean} input.isMuted This device's own "do not send" preference.
 * @param {string} input.installSupport How this browser installs, if at all.
 */
export function resolveNotificationSetting({
    status = NOTIFICATION_STATUS.unsupported,
    isMuted = false,
    installSupport = INSTALL_SUPPORT.unsupported,
} = {}) {
    const state = (() => {
        if (status === NOTIFICATION_STATUS.unsupported || status === NOTIFICATION_STATUS.error) {
            // Absent AND installable is iOS in a browser tab, where the API
            // appears only once the app is on the home screen. That is a step the
            // visitor can take, so it is a different answer from "your browser
            // cannot do this", which is not.
            return installSupport === INSTALL_SUPPORT.iosShare
                ? NOTIFICATION_SETTING.needsInstall
                : NOTIFICATION_SETTING.unavailable;
        }

        if (status === NOTIFICATION_STATUS.denied) {
            return NOTIFICATION_SETTING.blocked;
        }

        if (status === NOTIFICATION_STATUS.granted) {
            return isMuted ? NOTIFICATION_SETTING.paused : NOTIFICATION_SETTING.on;
        }

        return NOTIFICATION_SETTING.off;
    })();

    return {
        state,
        ...NOTIFICATION_COPY[state],
        // Asking is possible exactly once, and only from `default`.
        canTurnOn: state === NOTIFICATION_SETTING.off,
        // Pausing is ours to offer because it is ours to honour: it stops this
        // app sending, which is the only half of the arrangement a page owns.
        canPause: state === NOTIFICATION_SETTING.on,
        canResume: state === NOTIFICATION_SETTING.paused,
    };
}

export function resolveInstallSetting({ installSupport = INSTALL_SUPPORT.unsupported } = {}) {
    if (installSupport === INSTALL_SUPPORT.installed) {
        return { state: 'installed', stateKey: 'settings.install.state.installed', canInstall: false, howToKey: null };
    }

    if (installSupport === INSTALL_SUPPORT.unsupported) {
        return { state: 'unsupported', stateKey: 'settings.install.state.unsupported', canInstall: false, howToKey: null };
    }

    return {
        state: 'available',
        stateKey: 'settings.install.state.available',
        // Only Chromium hands the page a prompt to fire; everywhere else the
        // instruction is the whole of what we can offer (SPEC-016 D-6).
        canInstall: installSupport === INSTALL_SUPPORT.nativePrompt,
        howToKey: installHowToKey(installSupport),
    };
}

/**
 * The status list, as data.
 *
 * Modelled on habb.me's app-menu diagnostics, and cut down hard. That list
 * answers "why did my reminder not arrive" for a product with a push server
 * behind it; this one answers "is it on, and is it working", for a product whose
 * whole delivery mechanism is a diff against a static file. Rows nobody can act
 * on — the push manager's existence, the worker's scope string — are not
 * reassurance, they are noise with a technical accent.
 *
 * `null` values render as an em dash rather than as "null", and a row whose
 * value cannot be known is still shown: its absence would be the more confusing
 * answer.
 *
 * There is deliberately no "Notifications" row. The setting directly above this
 * list already states it, in larger type and beside the control that changes it,
 * and the same fact twice in one scroll is not a summary — it is noise that
 * makes a reader check whether the two agree.
 */
export function buildStatusRows({
    isStandalone = false,
    hasServiceWorker = false,
    hasBackgroundSync = false,
    knownPlantCount = 0,
    lastCheckedAt = null,
    appVersion = null,
} = {}) {
    return [
        { key: 'runningAsApp', labelKey: 'settings.status.runningAsApp', booleanValue: isStandalone },
        { key: 'offline', labelKey: 'settings.status.offline', booleanValue: hasServiceWorker },
        // Only meaningful where it can be true. On iOS there is no background
        // path at all (SPEC-016 D-10), and a permanent "No" on a row the visitor
        // cannot change reads as a fault rather than as a platform limit.
        { key: 'background', labelKey: 'settings.status.background', booleanValue: hasBackgroundSync, hintKey: hasBackgroundSync ? null : 'settings.status.background.hint' },
        { key: 'plantsKnown', labelKey: 'settings.status.plantsKnown', value: knownPlantCount > 0 ? String(knownPlantCount) : null },
        { key: 'lastChecked', labelKey: 'settings.status.lastChecked', timestamp: Number.isFinite(lastCheckedAt) && lastCheckedAt > 0 ? lastCheckedAt : null },
        { key: 'version', labelKey: 'settings.status.version', value: appVersion },
    ];
}
